import React, { useEffect, useRef, useState } from 'react';
import { BookOpen } from 'lucide-react';
import type { ChatSource } from '../../types';

interface KnowledgeCitationProps {
  label: string;
  source: ChatSource;
}

/** Citation marker for an uploaded-knowledge source: click shows the document, section and excerpt. */
export const KnowledgeCitation: React.FC<KnowledgeCitationProps> = ({ label, source }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false);
        return;
      }
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="relative inline-block align-baseline">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={`Source ${label}: ${source.title}`}
        className="mx-0.5 inline-flex h-5 min-w-5 translate-y-px items-center justify-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 text-[11px] font-semibold leading-none text-emerald-700 shadow-sm transition-colors hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20"
      >
        <span>{label}</span>
        <BookOpen size={10} strokeWidth={2.2} aria-hidden="true" />
      </button>
      {open && (
        <span
          role="dialog"
          className="absolute left-0 top-full z-30 mt-1 block w-72 max-w-[80vw] rounded-lg border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-700 shadow-lg dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          <span className="block font-semibold text-gray-900 dark:text-white">{source.title}</span>
          {source.section && (
            <span className="mt-0.5 block text-gray-500 dark:text-slate-400">{source.section}</span>
          )}
          {source.excerpt && (
            <span className="mt-2 block max-h-48 overflow-y-auto whitespace-pre-wrap border-l-2 border-emerald-300 pl-2 dark:border-emerald-500/50">
              {source.excerpt}
            </span>
          )}
        </span>
      )}
    </span>
  );
};
